import React, { useCallback, useMemo, useState } from 'react';

import { Collapse, Empty, Flex, Skeleton, Spin } from 'antd-v5';
import DOMPurify from 'dompurify';
import { MdUnfoldLess, MdUnfoldMore } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui';

import RequirementRow, {
  RequirementStatus,
  toPlainText,
} from './RequirementRow';
import styles from './RequirementsRenderer.module.scss';
import VersionPicker from './VersionPicker';
import { RequirementsProvider } from './context';
import type { RequirementNode, RequirementsRendererProps } from './types';
import { resolveRowPolicy } from './utils';

function PanelHeader(props: { requirement: RequirementNode }) {
  const { requirement } = props;
  const summary = requirement.short || toPlainText(requirement.name);

  return (
    <Flex align="center" gap="small" className={styles.panelHeader}>
      {requirement.listNumber && (
        <Text font="body-body-3" strong className={styles.listNumber}>
          {requirement.listNumber}
        </Text>
      )}
      <Text font="body-body-3" className={styles.panelSummary}>
        {summary}
      </Text>
      <RequirementStatus requirement={requirement} />
    </Flex>
  );
}

/**
 * A requirement checklist that renders into an ancestor `<Form>`.
 *
 * The consumer owns the form and its `initialValues`; this component owns no
 * fetch and no store. Each requirement saves on its own - see
 * `RequirementControls` for why Save is not a submit button
 */
export default function RequirementsRenderer(props: RequirementsRendererProps) {
  const {
    requirements,
    advancementType,
    loading,
    savingRequirementId,
    busy,
    disabledActions,
    isYouthEditor,
    disableRequirement,
    overrideContent,
    extraContent,
    disabledDate,
    completionDateRules,
    onSaveRequirement,
    notes,
    version,
  } = props;

  const allKeys = useMemo(
    () => requirements.map(requirement => String(requirement.id)),
    [requirements],
  );

  const keySignature = allKeys.join('|');

  // Requirements start expanded and re-expand when the id set changes. Adjusted
  // during render, not in an effect, so the list never paints collapsed first
  const [openedFor, setOpenedFor] = useState(keySignature);
  const [activeKeys, setActiveKeys] = useState<string[]>(allKeys);

  if (keySignature !== openedFor) {
    setOpenedFor(keySignature);
    setActiveKeys(allKeys);
  }

  // Membership, not count: after a version switch `activeKeys` can still hold the
  // outgoing version's ids, which a length comparison would read as expanded
  const allExpanded =
    allKeys.length > 0 && allKeys.every(key => activeKeys.includes(key));

  const toggleAll = useCallback(
    () => setActiveKeys(allExpanded ? [] : allKeys),
    [allExpanded, allKeys],
  );

  // Either half can be bare markup with no words in it, so emptiness is judged
  // on the rendered text
  const noteBlocks = useMemo(
    () =>
      [notes?.header, notes?.footer].filter(
        (note): note is string => Boolean(note) && Boolean(toPlainText(note!)),
      ),
    [notes?.header, notes?.footer],
  );

  const resolvePolicy = useCallback(
    (requirement: RequirementNode) =>
      resolveRowPolicy({
        requirement,
        advancementType,
        disabledActions,
        isYouthEditor,
        disableRequirement,
      }),
    [advancementType, disabledActions, isYouthEditor, disableRequirement],
  );

  const contextValue = useMemo(
    () => ({
      resolvePolicy,
      savingRequirementId,
      disabled: busy,
      overrideContent,
      extraContent,
      disabledDate,
      completionDateRules,
      onSaveRequirement,
    }),
    [
      resolvePolicy,
      savingRequirementId,
      busy,
      overrideContent,
      extraContent,
      disabledDate,
      completionDateRules,
      onSaveRequirement,
    ],
  );

  return (
    <RequirementsProvider value={contextValue}>
      <Flex vertical gap="middle" className={styles.container}>
        <Flex align="flex-end" gap="middle" wrap className={styles.toolbar}>
          {version && (
            <VersionPicker
              options={version.options}
              selectedVersionId={version.selectedVersionId}
              disabled={version.disabled}
              loading={version.changing}
              onChange={version.onChange}
            />
          )}

          {!loading && requirements.length > 0 && (
            <Button
              type="link"
              onClick={toggleAll}
              className={styles.expandToggle}
              icon={allExpanded ? <MdUnfoldLess /> : <MdUnfoldMore />}
            >
              <FormattedMessage
                id={
                  allExpanded
                    ? 'requirementsRenderer.collapseAll'
                    : 'requirementsRenderer.expandAll'
                }
              />
            </Button>
          )}
        </Flex>

        {/* Only the list swaps for the skeleton; the picker stays mounted so it
        does not reflow during a version switch */}
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : requirements.length === 0 ? (
          <Empty
            description={<FormattedMessage id="requirementsRenderer.empty" />}
          />
        ) : (
          // Spin only dims its children; `busy` also reaches the rows to lock them
          <Spin spinning={Boolean(busy)}>
            <Collapse
              className={styles.collapse}
              activeKey={activeKeys}
              onChange={keys =>
                setActiveKeys(Array.isArray(keys) ? keys : [keys])
              }
              items={requirements.map(requirement => ({
                key: String(requirement.id),
                label: <PanelHeader requirement={requirement} />,
                children: (
                  <RequirementRow
                    requirement={requirement}
                    root={requirement}
                    depth={0}
                  />
                ),
              }))}
            />
          </Spin>
        )}

        {!loading && noteBlocks.length > 0 && (
          <div className={styles.notes}>
            {noteBlocks.map(note => (
              <div
                key={note}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note) }}
              />
            ))}
          </div>
        )}
      </Flex>
    </RequirementsProvider>
  );
}
